"use client";

import { Maximize2 } from "lucide-react";
import { motion, type Variants } from "motion/react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { EASE_OUT_EXPO } from "@/components/motion/reveal";
import { Image } from "@/components/shared/image";
import type { MediaAsset } from "@/lib/media";
import { cn } from "@/lib/utils";

import { Lightbox } from "./lightbox";

// =============================================================================

const listVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const itemVariants: Variants = {
  hidden: { clipPath: "inset(100% 0% 0% 0% round 0.5rem)", y: 40 },
  visible: {
    clipPath: "inset(0% 0% 0% 0% round 0.5rem)",
    y: 0,
    transition: { duration: 1.2, ease: EASE_OUT_EXPO },
  },
};

const shotVariants: Variants = {
  hidden: { scale: 1.12, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 1.4, delay: 0.15, ease: EASE_OUT_EXPO },
  },
};

// =============================================================================

type ProjectGalleryProps = {
  projectName: string;
  images: MediaAsset[];
};

/**
 * Screenshots two by two (an odd last one spans the whole row). Each one
 * opens the lightbox.
 */
export function ProjectGallery({ projectName, images }: ProjectGalleryProps) {
  const t = useTranslations("Projects.detail");
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const total = images.length;

  return (
    <>
      <motion.ul
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "0px 0px -10% 0px" }}
        variants={listVariants}
        className="grid gap-4 sm:gap-5 md:grid-cols-2"
      >
        {images.map((image, index) => {
          const isWide = total % 2 === 1 && index === total - 1;
          return (
            <motion.li
              key={image.url}
              variants={itemVariants}
              className={cn(isWide && "md:col-span-2")}
            >
              <button
                type="button"
                onClick={() => setOpenIndex(index)}
                aria-label={t("openImage", { index: index + 1, total })}
                aria-haspopup="dialog"
                className={cn(
                  "group relative block w-full overflow-hidden rounded-lg bg-surface-3 p-[7.3%]",
                  isWide && "md:px-[20%] md:py-[4.5%]"
                )}
              >
                {/* Lighter panel on hover */}
                <span
                  aria-hidden
                  className="absolute inset-0 bg-foreground/4 opacity-0 transition-opacity duration-500 ease-out-quart group-hover:opacity-100"
                />
                <motion.span variants={shotVariants} className="block">
                  <span className="relative block aspect-video overflow-hidden rounded-[3px] shadow-[0_1.5rem_3rem_-1rem_rgb(0_0_0/0.55)] transition-transform duration-700 ease-out-expo group-hover:-translate-y-1.5 group-hover:scale-[1.025] motion-reduce:transition-none">
                    <Image
                      src={image.url}
                      alt=""
                      fill
                      sizes={
                        isWide
                          ? "(min-width: 768px) 56vw, 90vw"
                          : "(min-width: 768px) 40vw, 90vw"
                      }
                      className="object-cover object-top"
                    />
                  </span>
                </motion.span>
                <span
                  aria-hidden
                  className="absolute top-4 right-4 grid size-10 scale-75 place-items-center rounded-full bg-background/80 opacity-0 backdrop-blur transition-[opacity,transform] duration-500 ease-out-expo group-hover:scale-100 group-hover:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100"
                >
                  <Maximize2 className="size-4" />
                </span>
              </button>
            </motion.li>
          );
        })}
      </motion.ul>

      <Lightbox
        projectName={projectName}
        images={images}
        index={openIndex}
        onIndexChange={setOpenIndex}
      />
    </>
  );
}

// =============================================================================
